// src/features/records/services/accessControlService.js

/**
 * Access Control Service
 * 
 * This service handles all access control operations for records including:
 * - Granting and revoking access to users and groups
 * - Managing visibility settings
 * - Fetching current permissions
 * - Permission validation
 */

import { db, auth, functions } from '../../../firebase';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

export const ROLE_TO_LEVEL = Object.freeze({ view: 1, edit: 2, admin: 3 });
export const LEVEL_TO_ROLE = Object.freeze({ 1: 'view', 2: 'edit', 3: 'admin' });

const normalizeEdgeDocument = (recordId, raw) => {
  if (!raw) return null;

  const permission = raw.permission ?? {};
  const level = typeof permission.level === 'number'
    ? permission.level
    : (permission.role && ROLE_TO_LEVEL[permission.role]) || 0;
  const role = permission.role ?? LEVEL_TO_ROLE[level] ?? null;

  return {
    recordId: raw.recordId ?? recordId ?? null,
    ownerId: raw.ownerId ?? null,
    permission: {
      role,
      level,
      sources: permission.sources ?? {},
      sourcesGroupIds: permission.sourcesGroupIds ?? []
    },
    lifecycle: raw.lifecycle ?? 'active',
    viewFlags: raw.viewFlags ?? {},
    query: raw.query ?? {},
    preview: raw.preview ?? {},
    updatedAt: raw.updatedAt ?? null
  };
};

class AccessControlService {
  constructor() {
    // Initialize Cloud Functions
    this.grantRecordAccessFn = httpsCallable(functions, 'grantRecordAccess');
    this.revokeRecordAccessFn = httpsCallable(functions, 'revokeRecordAccess');
    this.setRecordVisibilityFn = httpsCallable(functions, 'setRecordVisibility');
    
    // Cache for permission checks
    this.permissionCache = new Map();
    this.permissionInflight = new Map();
    this.permissionCacheVersions = new Map();
    this.permissionCacheTtlMs = 5 * 60 * 1000; // 5 minutes
  }

  getPermissionCacheVersion(cacheKey) {
    return this.permissionCacheVersions.get(cacheKey) ?? 0;
  }

  bumpPermissionCacheVersion(cacheKey) {
    const nextVersion = this.getPermissionCacheVersion(cacheKey) + 1;
    this.permissionCacheVersions.set(cacheKey, nextVersion);
    return nextVersion;
  }

  /**
   * Check if current user has admin rights on a record
   * @param {Object} record - Record object
   * @returns {Promise<boolean>} True if user is admin
   */
  async isUserAdmin(record, options = {}) {
    if (!auth.currentUser || !record?.id) return false;

    const userId = auth.currentUser.uid;
    const { forceRefresh = false } = options;

    // Owner always has admin rights
    if (record.access?.owner === userId) {
      return true;
    }
    
    // Check user's accessible records index for admin permission
    try {
      const edge = await this.getUserPermission(record.id, userId, { forceRefresh });
      return edge?.permission?.level >= 3; // 3 = admin
    } catch (error) {
      console.error('Error checking admin permissions:', error);
    }
    
    return false;
  }

  /**
   * Grant access to a record
   * @param {string} recordId - Record ID
   * @param {Object} principal - Principal object with type and id
   * @param {string} role - Permission role (view/edit/admin)
   * @param {Object} [options] - Optional controls
   * @param {Object} [options.notify] - Optional notify payload
   * @returns {Promise<Object>} Result of the operation
   */
  async grantAccess(recordId, principal, role, options = {}) {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    try {
      const payload = {
        recordId,
        principal,
        role
      };

      if (options.notify !== undefined) {
        payload.notify = options.notify;
      }

      const result = await this.grantRecordAccessFn(payload);
      
      // Clear cache for this record
      this.clearPermissionCache(recordId);
      
      return result.data;
    } catch (error) {
      console.error('Error granting access:', error);
      throw new Error(error.message || 'Failed to grant access');
    }
  }

  /**
   * Revoke access from a record
   * @param {string} recordId - Record ID
   * @param {Object} principal - Principal object with type and id
   * @returns {Promise<Object>} Result of the operation
   */
  async revokeAccess(recordId, principal) {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    try {
      const result = await this.revokeRecordAccessFn({
        recordId,
        principal
      });
      
      // Clear cache for this record
      this.clearPermissionCache(recordId);
      
      return result.data;
    } catch (error) {
      console.error('Error revoking access:', error);
      throw new Error(error.message || 'Failed to revoke access');
    }
  }

  /**
   * Set record visibility
   * @param {string} recordId - Record ID
   * @param {string} visibility - Visibility setting (private/friends/public)
   * @returns {Promise<Object>} Result of the operation
   */
  async setVisibility(recordId, visibility) {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    try {
      const result = await this.setRecordVisibilityFn({
        recordId,
        visibility
      });
      
      // Clear cache for this record
      this.clearPermissionCache(recordId);
      
      return result.data;
    } catch (error) {
      console.error('Error setting visibility:', error);
      throw new Error(error.message || 'Failed to set visibility');
    }
  }

  /**
   * Fetch current grants for a record
   * @param {string} recordId - Record ID
   * @returns {Promise<Array>} Array of grant entries
   */
  async fetchGrants(recordId) {
    if (!auth.currentUser) {
      throw new Error('User must be authenticated');
    }

    try {
      const grantsRef = collection(db, 'records', recordId, 'grants');
      const snapshot = await getDocs(grantsRef);
      
      const grants = [];
      snapshot.forEach(doc => {
        grants.push({
          id: doc.id, // This is the principalKey
          ...doc.data()
        });
      });
      
      return grants;
    } catch (error) {
      console.error('Error fetching grants:', error);
      throw new Error('Failed to fetch permissions');
    }
  }

  /**
   * Subscribe to grants updates for a record
   * 
   * Note: Security rules only allow admins to read grants.
   * Call this only after confirming user has admin access via isUserAdmin()
   * 
   * @param {string} recordId - Record ID
   * @param {Function} callback - Callback for updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToGrants(recordId, callback) {
    if (!auth.currentUser) {
      console.error('User must be authenticated');
      return () => {};
    }

    const grantsRef = collection(db, 'records', recordId, 'grants');
    
    return onSnapshot(
      grantsRef,
      (snapshot) => {
        const grants = [];
        snapshot.forEach(doc => {
          grants.push({
            id: doc.id, // This is the principalKey
            ...doc.data()
          });
        });
        callback(grants);
      },
      (error) => {
        console.error('Error subscribing to grants:', error);
        // Security rules may block non-admins - return empty array
        callback([]);
      }
    );
  }

  /**
   * Get effective permission for a user on a record
   * @param {string} recordId - Record ID
   * @param {string} userId - User ID
   * @returns {Promise<Object|null>} Permission object or null
   */
  async getUserPermission(recordId, userId, options = {}) {
    const { forceRefresh = false } = options;
    if (!recordId || !userId) {
      return null;
    }

    const cacheKey = `${recordId}:${userId}`;
    if (forceRefresh) {
      this.permissionCache.delete(cacheKey);
      this.bumpPermissionCacheVersion(cacheKey);
    }

    // Check cache first with TTL validation
    const cached = this.permissionCache.get(cacheKey);
    if (
      !forceRefresh &&
      cached &&
      Date.now() - cached.__ts < this.permissionCacheTtlMs
    ) {
      return cached.data;
    }

    const inflight = this.permissionInflight.get(cacheKey);
    if (!forceRefresh && inflight) {
      return inflight;
    }

    const requestVersion = this.getPermissionCacheVersion(cacheKey);
    const fetchPromise = (async () => {
      try {
        // Read from user's accessible records index (allowed by security rules)
        const accessRef = doc(db, 'users', userId, 'accessibleRecords', recordId);
        const accessDoc = await getDoc(accessRef);

        if (accessDoc.exists()) {
          const normalized = normalizeEdgeDocument(recordId, accessDoc.data());

          // Only cache if this request is still current for this key.
          if (this.getPermissionCacheVersion(cacheKey) === requestVersion) {
            this.permissionCache.set(cacheKey, {
              __ts: Date.now(),
              data: normalized
            });
          }

          return normalized;
        }

        if (this.getPermissionCacheVersion(cacheKey) === requestVersion) {
          this.permissionCache.delete(cacheKey);
        }
      } catch (error) {
        console.error('Error fetching user permission:', error);
        if (this.getPermissionCacheVersion(cacheKey) === requestVersion) {
          this.permissionCache.delete(cacheKey);
        }
      }

      return null;
    })();

    this.permissionInflight.set(cacheKey, fetchPromise);
    try {
      return await fetchPromise;
    } finally {
      if (this.permissionInflight.get(cacheKey) === fetchPromise) {
        this.permissionInflight.delete(cacheKey);
      }
    }
  }

  /**
   * Subscribe to user's permission changes for a record
   * @param {string} recordId - Record ID
   * @param {string} userId - User ID
   * @param {Function} callback - Callback for permission updates
   * @returns {Function} Unsubscribe function
   */
  subscribeToUserPermission(recordId, userId, callback) {
    if (!userId || !recordId) {
      console.error('User ID and Record ID are required');
      return () => {};
    }

    // Read from user's accessible records index (allowed by security rules)
    const accessRef = doc(db, 'users', userId, 'accessibleRecords', recordId);

    return onSnapshot(
      accessRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const normalized = normalizeEdgeDocument(recordId, snapshot.data());
          // Update cache with timestamp
          const cacheKey = `${recordId}:${userId}`;
          this.permissionCache.set(cacheKey, {
            __ts: Date.now(),
            data: normalized
          });
          callback(normalized);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error subscribing to user permission:', error);
        // Pass error through to caller for proper handling
        callback({ __error: error });
      }
    );
  }

  /**
   * Clear permission cache for a record
   * @param {string} recordId - Record ID
   */
  clearPermissionCache(recordId) {
    const prefix = `${recordId}:`;
    const keysToClear = new Set();

    for (const key of this.permissionCache.keys()) {
      if (key.startsWith(prefix)) {
        keysToClear.add(key);
      }
    }
    for (const key of this.permissionInflight.keys()) {
      if (key.startsWith(prefix)) {
        keysToClear.add(key);
      }
    }
    for (const key of this.permissionCacheVersions.keys()) {
      if (key.startsWith(prefix)) {
        keysToClear.add(key);
      }
    }

    for (const key of keysToClear) {
      this.permissionCache.delete(key);
      this.permissionInflight.delete(key);
      this.bumpPermissionCacheVersion(key);
    }
  }

  /**
   * Format principal key for display
   * @param {string} principalKey - Principal key (e.g., "user:uid123", "group:gid456")
   * @returns {Object} Parsed principal with type and id
   */
  parsePrincipalKey(principalKey) {
    const [type, ...idParts] = principalKey.split(':');
    return {
      type,
      id: idParts.join(':') // Handle IDs that might contain colons
    };
  }

  /**
   * Create principal key from type and id
   * @param {string} type - Principal type (user/group/public)
   * @param {string} id - Principal ID
   * @returns {string} Principal key
   */
  createPrincipalKey(type, id) {
    return `${type}:${id}`;
  }
}

// Export singleton instance
const accessControlService = new AccessControlService();
export default accessControlService;
